import React, { useEffect, useState } from 'react';
import { getUiLocale } from '@/lib/dates';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Save, Paperclip, FileText, X } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const wordCount = (s) => (s || '').trim().split(/\s+/).filter(Boolean).length;

export default function ClientTalentFixContext() {
  const { id: caseId } = useParams();
  const [summary, setSummary] = useState(null);
  const [note, setNote] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, n] = await Promise.all([
          api.get(`/client/talentfix/cases/${caseId}/summary`),
          api.get(`/client/talentfix/cases/${caseId}/context-note`).catch(() => ({ data: {} })),
        ]);
        setSummary(s.data);
        if (n.data?.context_note) {
          setNote(n.data.context_note);
          setAttachments(n.data.context_attachments || []);
        }
      } catch (e) {
        toast.error('Could not load case');
      } finally { setLoading(false); }
    })();
  }, [caseId]);

  const addAttachment = (e) => {
    const files = Array.from(e.target.files || []);
    // For Phase 1 we store metadata only — full object-storage upload arrives in Phase 4.
    const refs = files.map((f) => ({
      filename: f.name, mime: f.type, size_bytes: f.size,
      url: null,  // populated by object-storage upload in Phase 4
    }));
    setAttachments([...attachments, ...refs]);
    toast.message(`${files.length} attachment(s) queued (upload wires in Phase 4)`);
    e.target.value = '';
  };

  const handleSave = async () => {
    if (!note.trim()) { toast.error('Add context before saving'); return; }
    setSaving(true);
    try {
      await api.post(`/client/talentfix/cases/${caseId}/context-note`, {
        context_note: note,
        context_attachments: attachments,
      });
      toast.success('Context submitted to HM Advisor');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-sm text-slate-500">Loading…</div>;

  const wc = wordCount(note);
  const wcTone = wc > 5000 ? 'text-rose-600' : wc > 4500 ? 'text-amber-600' : 'text-slate-500';

  return (
    <div className="space-y-6 p-6" data-testid="tf2-client-context-page">
      <div>
        <Link to={`/client/talentfix/cases/${caseId}`}
              className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft className="h-4 w-4" />Back to case
        </Link>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight">Case context — for HM Advisor</h1>
        <p className="text-sm text-slate-500">
          Share full background, prior context, and any supporting documents so the consultant arrives prepared.
        </p>
      </div>

      {/* Transparency card — stage, advisor, milestones */}
      <Card data-testid="tf2-client-transparency">
        <CardHeader>
          <CardTitle>Where this case stands</CardTitle>
          <CardDescription>Visible to you at all times — your case is never invisible.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
          <div><p className="text-xs text-slate-500">Stage</p><Badge>{summary?.stage || '-'}</Badge></div>
          <div><p className="text-xs text-slate-500">Case status</p><p>{summary?.status || '-'}</p></div>
          <div><p className="text-xs text-slate-500">Assigned HM Advisor</p>
               <p>{summary?.assigned_advisor_name || '— not yet assigned —'}</p></div>
          <div><p className="text-xs text-slate-500">Diagnostic plan</p>
               <p>{summary?.diagnostic_plan_status || 'not started'}</p></div>
          <div><p className="text-xs text-slate-500">Next planned activity</p>
               <p>{summary?.next_planned_activity || '—'}</p></div>
          <div><p className="text-xs text-slate-500">Estimated completion</p>
               <p>{summary?.estimated_completion || '—'}</p></div>
        </CardContent>
      </Card>

      {/* Context note + attachments */}
      <Card>
        <CardHeader>
          <CardTitle>Context note</CardTitle>
          <CardDescription>
            Up to 5,000 words. Include prior interventions, organisational changes, recent events, and anything else
            that gives the consultant the full picture.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea data-testid="tf2-context-note" rows={18}
            value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Begin with the problem context, then add background, prior interventions, and anything else the HM Advisor should know…" />
          <p className={`text-xs ${wcTone}`}>{wc.toLocaleString(getUiLocale())} / 5,000 words</p>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-sm font-medium text-slate-700">Supporting attachments</label>
              <label className="cursor-pointer">
                <input type="file" multiple className="hidden" onChange={addAttachment}
                  data-testid="tf2-context-attach-input" />
                <span className="inline-flex items-center gap-1 rounded border border-slate-300 px-3 py-1.5 text-xs hover:bg-slate-50">
                  <Paperclip className="h-3.5 w-3.5" />Add files
                </span>
              </label>
            </div>
            <ul className="space-y-1.5">
              {attachments.map((a, i) => (
                <li key={i} className="flex items-center justify-between rounded border border-slate-200 px-3 py-2 text-sm">
                  <span className="flex items-center gap-2 truncate">
                    <FileText className="h-3.5 w-3.5" />{a.filename}
                    <span className="text-xs text-slate-400">
                      {(a.size_bytes / 1024).toFixed(0)} KB
                    </span>
                  </span>
                  <Button size="icon" variant="ghost"
                    onClick={() => setAttachments(attachments.filter((_, j) => j !== i))}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </li>
              ))}
              {attachments.length === 0 && (
                <li className="text-xs text-slate-500">No attachments yet.</li>
              )}
            </ul>
          </div>

          <div className="flex items-center justify-end pt-2">
            <Button onClick={handleSave} disabled={saving} data-testid="tf2-context-save">
              <Save className="mr-1.5 h-4 w-4" />
              {saving ? 'Saving…' : 'Save & submit'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
